import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { getCurrentMemberId } from '@/lib/auth/current-member-id';
import { fetchBlock, fetchCurrentMember } from '@/lib/blocks/api';
import { findFamilyRoot, findPersonalRoot } from '@/lib/blocks/constants';
import type { BlockRecord } from '@/lib/blocks/types';
import {
  loadSidebarNavBlocks,
  loadSidebarNavChildren,
  reloadSidebarNavBlocks,
} from '@/lib/layout/load-sidebar-nav';
import { getSidebarExpanded, setSidebarExpanded } from '@/lib/layout/sidebar-expanded';
import { buildSidebarChildrenIndex, navRootBlocks } from '@/lib/layout/sidebar-nav-index';
import { buildNavSections, type NavSection } from '@/lib/layout/sidebar-nav';
import { getFavoritePages, loadFavorites, subscribeFavorites } from '@/lib/favorites/favorites-api';
import { loadLlmPrivate } from '@/lib/llm-private/llm-private-api';
import type { FavoritePage } from '@/lib/favorites/types';
import { WORKSPACE_WIPED_EVENT } from '@/lib/dev/cleanup';
import { getSyncEngine } from '@/lib/sync/engine';
import {
  getRecentPagesForSidebar,
  subscribeRecentPages,
  type RecentPage,
} from '@/lib/navigation/recent-pages';

export type { NavSection };

type SidebarContextValue = {
  navSections: NavSection[];
  recentPages: RecentPage[];
  favoritePages: FavoritePage[];
  loading: boolean;
  error: string | null;
  childrenByParent: Record<string, BlockRecord[]>;
  expanded: Record<string, boolean>;
  ensureChildren: (parentId: string) => Promise<BlockRecord[]>;
  addChild: (parentId: string, block: BlockRecord) => void;
  removeChild: (parentId: string, blockId: string) => void;
  updateChild: (parentId: string, blockId: string, patch: Partial<BlockRecord>) => void;
  setChildren: (parentId: string, blocks: BlockRecord[]) => void;
  invalidateChildren: (parentId: string) => void;
  toggleExpanded: (id: string, defaultExpanded?: boolean) => void;
  expandToPage: (pageId: string) => Promise<void>;
  /** Reload sidebar roots and drop cached child lists (e.g. after bulk import). */
  refreshTree: () => void;
  refreshRoots: () => void;
};

const SidebarContext = createContext<SidebarContextValue | null>(null);

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [navSections, setNavSections] = useState<NavSection[]>(() =>
    buildNavSections({ familyRootId: null, privateRootId: null }),
  );
  const [recentPages, setRecentPages] = useState<RecentPage[]>(() => getRecentPagesForSidebar());
  const [favoritePages, setFavoritePages] = useState<FavoritePage[]>(() => getFavoritePages());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [childrenByParent, setChildrenByParent] = useState<Record<string, BlockRecord[]>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => getSidebarExpanded());
  const [refreshToken, setRefreshToken] = useState(0);
  const childrenRef = useRef<Record<string, BlockRecord[]>>({});
  const navBlocksRef = useRef<BlockRecord[]>([]);
  const navRequestRef = useRef(0);
  const expandRequestRef = useRef(0);
  const memberIdRef = useRef<string | null>(getCurrentMemberId());
  const childrenInflightRef = useRef(new Map<string, Promise<BlockRecord[]>>());
  const bootstrapSidebarRefreshDoneRef = useRef(false);

  function persistExpanded(next: Record<string, boolean>): void {
    setSidebarExpanded(next);
  }

  const writeChildrenCache = useCallback((parentId: string, pages: BlockRecord[]) => {
    childrenRef.current = { ...childrenRef.current, [parentId]: pages };
    setChildrenByParent(childrenRef.current);
  }, []);

  const applyNavBlocks = useCallback((blocks: BlockRecord[], memberId: string | null) => {
    navBlocksRef.current = blocks;
    const children = buildSidebarChildrenIndex(blocks);
    childrenRef.current = children;
    setChildrenByParent(children);
    const roots = navRootBlocks(blocks);
    setNavSections(
      buildNavSections({
        familyRootId: findFamilyRoot(roots)?.id ?? null,
        privateRootId: memberId ? (findPersonalRoot(roots, memberId)?.id ?? null) : null,
      }),
    );
  }, []);

  const loadRoots = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) {
      setLoading(true);
      void fetchCurrentMember()
        .then((member) => {
          if (memberIdRef.current === member.id) {
            return;
          }
          memberIdRef.current = member.id;
          const roots = navRootBlocks(navBlocksRef.current);
          if (roots.length === 0) {
            return;
          }
          setNavSections(
            buildNavSections({
              familyRootId: findFamilyRoot(roots)?.id ?? null,
              privateRootId: findPersonalRoot(roots, member.id)?.id ?? null,
            }),
          );
        })
        .catch(() => {
          // Personal root stays on the cached member id until the next successful read.
        });
    }
    setError(null);
    const requestId = ++navRequestRef.current;
    try {
      const blocks = await loadSidebarNavBlocks();
      if (requestId !== navRequestRef.current) {
        return;
      }
      const memberId = memberIdRef.current ?? getCurrentMemberId();
      memberIdRef.current = memberId;
      applyNavBlocks(blocks, memberId);
      setRecentPages(getRecentPagesForSidebar());
      void loadFavorites();
      void loadLlmPrivate();
    } catch (err) {
      if (requestId === navRequestRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load sidebar');
      }
    } finally {
      if (!opts?.silent) {
        setLoading(false);
      }
    }
  }, [applyNavBlocks]);

  useEffect(() => {
    void loadRoots({ silent: refreshToken > 0 });
  }, [loadRoots, refreshToken]);

  const refreshSidebarFromLocalDb = useCallback(async () => {
    const requestId = ++navRequestRef.current;
    try {
      const blocks = await reloadSidebarNavBlocks();
      if (requestId !== navRequestRef.current) {
        return;
      }
      applyNavBlocks(blocks, memberIdRef.current ?? getCurrentMemberId());
    } catch {
      // Keep the current tree on a transient read failure.
    }
  }, [applyNavBlocks]);

  // The sidebar tree is derived from the local DB but was previously loaded once
  // and cached, so pages synced from another device never appeared until reload.
  // Refresh when remote deltas land AND when sync first becomes ready (covers the
  // common case where the DB is already up to date and catchup applies nothing).
  useEffect(() => {
    const engine = getSyncEngine();
    let timer: ReturnType<typeof setTimeout> | null = null;
    let lastStatus = engine.getStatus();

    const scheduleRefresh = () => {
      if (timer) {
        clearTimeout(timer);
      }
      timer = setTimeout(() => {
        void refreshSidebarFromLocalDb();
      }, 400);
    };

    const markBootstrapRefresh = () => {
      if (bootstrapSidebarRefreshDoneRef.current) {
        return;
      }
      bootstrapSidebarRefreshDoneRef.current = true;
      scheduleRefresh();
    };

    const unsubscribeRemote = engine.subscribeRemote(scheduleRefresh);
    const unsubscribeStatus = engine.subscribe(() => {
      const status = engine.getStatus();
      if (status === 'ready' && lastStatus !== 'ready') {
        markBootstrapRefresh();
        // Initial sidebar load can race sync bootstrap and surface a transient error.
        if (error) {
          setRefreshToken((t) => t + 1);
        }
      }
      lastStatus = status;
    });

    if (engine.getStatus() === 'ready') {
      markBootstrapRefresh();
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
      unsubscribeRemote();
      unsubscribeStatus();
    };
  }, [error, refreshSidebarFromLocalDb]);

  useEffect(() => {
    return subscribeRecentPages(() => {
      setRecentPages(getRecentPagesForSidebar());
    });
  }, []);

  useEffect(() => {
    return subscribeFavorites(() => {
      setFavoritePages(getFavoritePages());
    });
  }, []);

  const ensureChildren = useCallback(
    async (parentId: string): Promise<BlockRecord[]> => {
      if (Object.prototype.hasOwnProperty.call(childrenRef.current, parentId)) {
        return childrenRef.current[parentId] ?? [];
      }
      const inflight = childrenInflightRef.current.get(parentId);
      if (inflight) {
        return inflight;
      }
      const promise = (async () => {
        const pages = await loadSidebarNavChildren(parentId);
        writeChildrenCache(parentId, pages);
        return pages;
      })();
      childrenInflightRef.current.set(parentId, promise);
      try {
        return await promise;
      } finally {
        childrenInflightRef.current.delete(parentId);
      }
    },
    [writeChildrenCache],
  );

  const addChild = useCallback((parentId: string, block: BlockRecord) => {
    const next = {
      ...childrenRef.current,
      [parentId]: [...(childrenRef.current[parentId] ?? []), block],
    };
    if (block.type === 'page' && next[block.id] === undefined) {
      next[block.id] = [];
    }
    childrenRef.current = next;
    setChildrenByParent(next);
    setExpanded((prev) => {
      const merged = { ...prev, [parentId]: true, [block.id]: false };
      persistExpanded(merged);
      return merged;
    });
  }, []);

  const removeChild = useCallback((parentId: string, blockId: string) => {
    // Drop a sidebar reload that started before this delete so it cannot paint the row back.
    navRequestRef.current += 1;
    const next = {
      ...childrenRef.current,
      [parentId]: (childrenRef.current[parentId] ?? []).filter((block) => block.id !== blockId),
    };
    childrenRef.current = next;
    setChildrenByParent(next);
  }, []);

  const updateChild = useCallback(
    (parentId: string, blockId: string, patch: Partial<BlockRecord>) => {
      const next = {
        ...childrenRef.current,
        [parentId]: (childrenRef.current[parentId] ?? []).map((block) =>
          block.id === blockId ? { ...block, ...patch } : block,
        ),
      };
      childrenRef.current = next;
      setChildrenByParent(next);
    },
    [],
  );

  const setChildren = useCallback((parentId: string, blocks: BlockRecord[]) => {
    const next = { ...childrenRef.current, [parentId]: blocks };
    childrenRef.current = next;
    setChildrenByParent(next);
  }, []);

  const invalidateChildren = useCallback((parentId: string) => {
    const next = { ...childrenRef.current };
    delete next[parentId];
    childrenRef.current = next;
    setChildrenByParent(next);
  }, []);

  const toggleExpanded = useCallback((id: string, defaultExpanded = false) => {
    setExpanded((prev) => {
      const current = prev[id] ?? defaultExpanded;
      const next = { ...prev, [id]: !current };
      persistExpanded(next);
      return next;
    });
  }, []);

  const expandToPage = useCallback(
    async (pageId: string): Promise<void> => {
      const requestId = ++expandRequestRef.current;
      const ancestorIds: string[] = [];

      try {
        let current = await fetchBlock(pageId);
        while (current.parentId) {
          ancestorIds.unshift(current.parentId);
          current = await fetchBlock(current.parentId);
        }
      } catch {
        return;
      }

      if (requestId !== expandRequestRef.current) {
        return;
      }

      for (const id of ancestorIds) {
        await ensureChildren(id);
        if (requestId !== expandRequestRef.current) {
          return;
        }
      }

      setExpanded((prev) => {
        const next = { ...prev };
        for (const id of ancestorIds) {
          next[id] = true;
        }
        persistExpanded(next);
        return next;
      });
    },
    [ensureChildren],
  );

  const refreshTree = useCallback(() => {
    childrenRef.current = {};
    setChildrenByParent({});
    setRefreshToken((t) => t + 1);
  }, []);

  const refreshRoots = refreshTree;

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const onWorkspaceWiped = () => {
      refreshTree();
    };
    window.addEventListener(WORKSPACE_WIPED_EVENT, onWorkspaceWiped);
    return () => window.removeEventListener(WORKSPACE_WIPED_EVENT, onWorkspaceWiped);
  }, [refreshTree]);

  const value = useMemo(
    () => ({
      navSections,
      recentPages,
      favoritePages,
      loading,
      error,
      childrenByParent,
      expanded,
      ensureChildren,
      addChild,
      removeChild,
      updateChild,
      setChildren,
      invalidateChildren,
      toggleExpanded,
      expandToPage,
      refreshTree,
      refreshRoots,
    }),
    [
      navSections,
      recentPages,
      favoritePages,
      loading,
      error,
      childrenByParent,
      expanded,
      ensureChildren,
      addChild,
      removeChild,
      updateChild,
      setChildren,
      invalidateChildren,
      toggleExpanded,
      expandToPage,
      refreshTree,
    ],
  );

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

export function useSidebar(): SidebarContextValue {
  const ctx = useContext(SidebarContext);
  if (!ctx) {
    throw new Error('useSidebar must be used within SidebarProvider');
  }
  return ctx;
}
